import { categories } from "@sobidogam/catalog";
import {
	formatKstDateTime,
	parsePaymentNotificationText,
} from "@sobidogam/domain";
import type { Channel } from "@sobidogam/platform-ports";
import { Header, navigate, SiteFooter } from "./chrome";

/**
 * Public first screen. The owner of the first viewport is a real card
 * approval message, the record it becomes, and the three places that record
 * lands. The example text is parsed by the same parser the app uses, so the
 * card below the bubble is real output, not a mock-up.
 */
const SAMPLE_NOTIFICATION = "[현대카드] 12,300원 승인 10/16 08:42 동네마트";

function sampleRecord() {
	const parsed = parsePaymentNotificationText(
		SAMPLE_NOTIFICATION,
		new Date("2026-10-16T00:00:00+09:00"),
	).records[0];
	return {
		store: parsed?.storeDisplay ?? "동네마트",
		amount: parsed?.totalAmount ?? 12300,
		at: parsed?.transactedAt ?? "2026-10-16T08:42:00+09:00",
	};
}

const GUIDES = [
	{
		href: "/guide/iphone-auto-entry",
		label: "아이폰에서 결제 알림 자동입력처럼 쓰기",
	},
	{
		href: "/guide/move-from-other-ledger",
		label: "위플·편한가계부 기록 옮기기",
	},
	{ href: "/guide/excel-ledger", label: "가계부 엑셀 양식 무료로 받고 옮기기" },
	{ href: "/guide/household-restock", label: "생필품 리스트를 장보기와 잇기" },
	{ href: "/guide/living-cost", label: "월급날 기준으로 생활비 관리하기" },
];

export function Landing({
	channel,
	onRecord,
}: {
	channel: Channel;
	onRecord?: () => void;
}) {
	const record = sampleRecord();
	const household = categories.find(
		(category) => category.categoryId === "daily-supermarket",
	);
	return (
		<>
			<Header channel={channel} />
			<main className="public-main landing">
				<section className="landing-hero section-wrap">
					<h1 className="landing-title">
						결제 알림 한 건이면
						<br />
						오늘 남은 돈과 살림이 바뀌어요
					</h1>
					<ol
						className="landing-flow"
						aria-label="결제 알림이 기록이 되는 순서"
					>
						<li className="sms-bubble">
							<small>카드 승인 문자</small>
							<p>{SAMPLE_NOTIFICATION}</p>
						</li>
						<li className="receipt-slip">
							<div className="receipt-slip-row">
								<strong>{record.store}</strong>
								<b className="amount">
									{record.amount.toLocaleString("ko-KR")}원
								</b>
							</div>
							<small>
								{household?.nameKo ?? "마트"} · {formatKstDateTime(record.at)}
							</small>
						</li>
						<li className="landing-outcomes">
							<p>
								<span>생활비</span>
								<b>먹고 마신 생활비에 12,300원 더해짐</b>
							</p>
							<p className="tag-band">
								<span>장보기</span>
								<b>영수증에 휴지를 적으면 장보기에서 지워져요</b>
							</p>
							<p>
								<span>도감</span>
								<b>{household?.nameKo ?? "마트"} 칸 1회 수집</b>
							</p>
						</li>
					</ol>
					<div className="landing-actions">
						<a
							className="button landing-cta"
							href="/app/capture"
							onClick={(event) => {
								event.preventDefault();
								if (onRecord) onRecord();
								else navigate("/app/capture");
							}}
						>
							결제 알림 붙여 넣기
						</a>
						<a
							className="text-link"
							href="/app/capture?mode=manual"
							onClick={(event) => {
								event.preventDefault();
								navigate("/app/capture?mode=manual");
							}}
						>
							알림이 없으면 직접 적기
						</a>
					</div>
					<p className="quiet-note">
						알림 원문은 이 기기에서만 읽어요 · 가입 없이 시작
					</p>
				</section>
				<section
					className="landing-compare section-wrap"
					aria-label="다른 방법과 비교"
				>
					<h2>지금 쓰는 방법과 비교하면</h2>
					<dl>
						<div>
							<dt>카드 앱 내역</dt>
							<dd>쓴 곳만 보여요. 산 물건과 다음 장보기는 남지 않아요.</dd>
						</div>
						<div>
							<dt>카톡 나에게 보내기</dt>
							<dd>
								다 쓴 물건을 매번 다시 적어요. 소비도감은 다 썼어 1번이면
								장보기에 올라가요.
							</dd>
						</div>
						<div>
							<dt>엑셀 가계부</dt>
							<dd>
								합계 수식과 월 경계를 직접 맞춰요. 소비도감은 월급날 기준
								합계·달력·그래프가 자동이에요.
							</dd>
						</div>
					</dl>
				</section>
				{channel === "web" ? (
					<section
						className="landing-guides section-wrap"
						aria-label="사용 안내"
					>
						<h2>이럴 때 읽어 보세요</h2>
						<ul>
							{GUIDES.map((guide) => (
								<li key={guide.href}>
									<a href={guide.href}>{guide.label}</a>
								</li>
							))}
						</ul>
					</section>
				) : null}
				<section className="landing-dogam section-wrap">
					<h2>기록할수록 128칸 도감이 채워져요</h2>
					<p>
						카페·마트·약국처럼 업종마다 그림이 달라요. 도감을 채우려고 따로
						입력할 필요는 없어요.
					</p>
					<a
						className="text-link"
						href="/dogam"
						onClick={(event) => {
							event.preventDefault();
							navigate("/dogam");
						}}
					>
						128칸 전체 보기
					</a>
				</section>
			</main>
			<SiteFooter />
		</>
	);
}
